/**
 * deployment-common.js
 */

import React, { useContext } from 'react';
import {
  Grid,
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import TextField from 'components/form-inputs/text-field';
import AutocompleteObject from 'components/form-inputs/autocomplete/autocomplete-object';
import Select from 'components/form-inputs/select';
import WorkMode from 'components/form-inputs/work-mode';
import { DogsContext } from 'contexts/dogs/dogs-context';
import DeploymentEditTags from './deployment-edit-tags';

export default function DeploymentCommon(props) {
  const {
    drawerState,
    edit,
  } = props;
  const translation = edit.translation;
  const { t } = useTranslation(translation);
  const { control, setValue, getValues } = useFormContext();
  const { getActiveDogs } = useContext(DogsContext);
  const workMode = useWatch({ control, name: 'workMode' });
  const dateValue = useWatch({ control, name: 'date' });
  const activeDogs = React.useMemo(() => getActiveDogs(dateValue), [getActiveDogs, dateValue]);

  // Request fulfillment options
  const fulfillmentStateOptions = [
    { id: Types.FulfillmentState.DogDeployed, name: 'Dog Deployed At Scene' },
    { id: Types.FulfillmentState.DogNotDeployed, name: 'Dog Not Deployed At Scene' },
    { id: Types.FulfillmentState.CancelledEnroute, name: 'Request Cancelled Enroute' }
  ];

  // only allow dog options that are/were active on selected date
  React.useEffect(() => {
    const selectedDog = getValues('dog');
    
    // ensure the selected dog is valid for the available options
    if (activeDogs.length === 0) {
      setValue('dog', '', { shouldDirty: true, shouldValidate: true });
    } else if (!selectedDog || !activeDogs.find(dog => dog.id === selectedDog.id)) {
      setValue('dog', activeDogs[0], { shouldDirty: true, shouldValidate: true });
    }
  }, [activeDogs, setValue, getValues]);


  return (
    <>
      <EditorIconSection
        iconName='summary'
        helpProps={{
          translation,
          section: 'info'
        }}
      >

        {/* case number */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.name.helpTitle')}
          text={t('info.name.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem md={7}>
                <TextField
                  name='caseNumber'
                  label={t('info.name.label')}
                  focusHandlers={focusHandlers}
                  rules={{
                    maxLength: {
                      value: 150,
                      message: t('info.name.errMsgLength')
                    }
                  }}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>

        {/* tags */}
        <DeploymentEditTags
          options={edit?.options?.deploymentTags}
          drawerState={drawerState}
          translation={translation}
        />

        {/* requesting unit */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.requestingUnit.helpTitle')}
          text={t('info.requestingUnit.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem md={7}>
                <AutocompleteObject
                  name='requestingUnit'
                  label={t('info.requestingUnit.label')}
                  focusHandlers={focusHandlers}
                  options={edit.options.requestingUnits}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>

        {/* dog */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.dog.helpTitle')}
          text={t('info.dog.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem md={7}>
                <Select
                  name='dog'
                  label={t('info.dog.label')}
                  focusHandlers={focusHandlers}
                  defaultValue=''
                  options={activeDogs}
                  setValueAs={id => ({ id })}
                  rules={{ required: t('info.dog.errMsgDate') }}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>

        {/* fulfillment state */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.fulfillmentState.helpTitle') || 'Request Fulfillment'}
          text={t('info.fulfillmentState.helpInfo') || 'Select the fulfillment status for this deployment request.'}
        >
          {focusHandlers =>
            <FormGridContainer>
              <FormGridItem md={7}>
                <Select
                  name='fulfillmentState'
                  label={t('info.fulfillmentState.label') || 'Request Fulfillment'}
                  focusHandlers={focusHandlers}
                  defaultValue={Types.FulfillmentState.DogDeployed}
                  options={fulfillmentStateOptions}
                  rules={{ required: t('info.fulfillmentState.errMsg') || 'Request fulfillment is required' }}
                />
              </FormGridItem>
            </FormGridContainer>}
        </HelpTextItem>

        {/* work mode */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.workMode.helpTitle')}
          text={t('info.workMode.helpInfo')}
        >
          <FormGridContainer>
            <FormGridItem md={7}>
              <WorkMode />
            </FormGridItem>
          </FormGridContainer>
        </HelpTextItem>

        {/* deployment reason */}
        {workMode === Types.WorkMode.PATROL &&
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.deploymentReason.helpTitle')}
          text={t('info.deploymentReason.helpInfo')}
        >
          {focusHandlers =>
            <FormGridContainer>
              <Grid item xs={12}>
                <TextField
                  name='deploymentReason'
                  label={t('info.deploymentReason.label')}
                  focusHandlers={focusHandlers}
                  multiline
                  rows={5}
                />
              </Grid>
            </FormGridContainer>}
        </HelpTextItem>}

      </EditorIconSection>
    </>
  );
}